import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
    colors,
    CustomButton,
    ModalComp,
    SelectComp,
    TextEditor,
    TextInputComp,
} from '../../common/component';
import { showSuccessToast, showErrorToast } from '../../common/utils/toast';
import {
    useGetSingleTopicQuery,
    useUpdateTopicMutation,
} from '../../store/topicApi';
const parse = require('html-react-parser');

export const TopicDetails = () => {
    const param = useParams();
    const topicId = param?.id as string;
    const [showModal, setShowModal] = useState(false);
    const { data: topicData } = useGetSingleTopicQuery(topicId);
    const [updateTopic, { isLoading: isUpdating }] = useUpdateTopicMutation();

    const { handleSubmit, setValue, control } = useForm({
        defaultValues: {
            newData: {
                title: '',
                description: '',
                status: '',
                priority: 0,
            },
        },
    });

    useEffect(() => {
        if (topicData?.data?._id) {
            setValue('newData', {
                title: topicData?.data?.title,
                description: topicData?.data?.description,
                status: topicData?.data?.status ? 'Active' : 'Inactive',
                priority: topicData?.data?.priority,
            });
        }
    });

    const onHandleEdit = (value: {
        newData: {
            title: string;
            description: string;
            status: string;
            priority: number;
        };
    }) => {
        const { title, description, status, priority } = value?.newData;
        let payload = {
            title: title,
            description: description,
            priority: Number(priority),
            topicId: topicId,
            status: status === 'Active' ? true : false,
        };

        updateTopic(payload)
            .unwrap()
            .then((result) => {
                showSuccessToast(result?.message);
                setShowModal(false);
            })
            .catch((error) => {
                showErrorToast(error?.data?.message);
            });
    };

    return (
        <div className="d-flex flex-column gap-4 mx-lg-5 mb-4">
            <>
                <div className="d-flex justify-content-between">
                    <Wrap>
                        <Title>Topic title</Title>
                        <Paragraph>{parse(topicData?.data?.title)}</Paragraph>
                    </Wrap>
                </div>
                <Wrap>
                    <Title>Topic description</Title>
                    <TextArea>{parse(topicData?.data?.description)}</TextArea>
                </Wrap>

                <div className="d-flex justify-content-center">
                    <CustomButton
                        className="py-3 mt-4"
                        text="Edit topic"
                        onClick={() => {
                            setShowModal(true);
                        }}
                    />
                </div>
            </>

            <ModalComp
                show={showModal}
                handleClose={() => setShowModal(false)}
                centered
                close={true}
                size="lg"
                modalTitle="Edit topic"
                paragraph="Fill in the forms to edit a topic"
                modalBody={
                    <form onSubmit={handleSubmit(onHandleEdit)}>
                        <div className="py-1 px-lg-4 ">
                            <div className="my-3">
                                <Controller
                                    name="newData.priority"
                                    control={control}
                                    render={({
                                        field: { value, onChange },
                                    }) => {
                                        return (
                                            <TextInputComp
                                                labelText="Priority"
                                                inputClassName={'py-2'}
                                                name="priority"
                                                type="number"
                                                defaultValue={1}
                                                {...{ value, onChange }}
                                            />
                                        );
                                    }}
                                />
                            </div>
                            <div className="my-4">
                                <Controller
                                    name="newData.title"
                                    control={control}
                                    rules={{
                                        required: 'Topic title is required',
                                    }}
                                    render={({
                                        field: { value, onChange },
                                    }) => {
                                        return (
                                            <TextEditor
                                                {...{ value, onChange }}
                                                labelText="Topic title"
                                                defaultValue={
                                                    topicData?.data?.title
                                                }
                                            />
                                        );
                                    }}
                                />
                            </div>
                            <div className="my-4">
                                <Controller
                                    name="newData.description"
                                    control={control}
                                    rules={{
                                        required:
                                            'Topic description is required',
                                    }}
                                    render={({
                                        field: { value, onChange },
                                    }) => {
                                        return (
                                            <TextEditor
                                                {...{ value, onChange }}
                                                labelText="Topic description"
                                                defaultValue={
                                                    topicData?.data?.description
                                                }
                                            />
                                        );
                                    }}
                                />
                            </div>
                            <Controller
                                name="newData.status"
                                control={control}
                                rules={{
                                    required: 'status is required',
                                }}
                                render={({
                                    field: { value, onChange },
                                    formState: { errors },
                                }) => {
                                    const errorMessage =
                                        errors?.newData?.status?.message;
                                    return (
                                        <SelectComp
                                            labelText="Status"
                                            inputClassName={'py-2 '}
                                            arrayComp={['Active', 'Inactive']}
                                            name="status"
                                            defaultValue={
                                                topicData?.data?.status
                                                    ? 'Active'
                                                    : 'Inactive'
                                            }
                                            {...{
                                                value,
                                                onChange,
                                                errors: [errorMessage],
                                            }}
                                        />
                                    );
                                }}
                            />

                            <div className="d-flex justify-content-center">
                                <CustomButton
                                    className="py-3 mt-4"
                                    text={
                                        isUpdating
                                            ? 'Loading...'
                                            : 'Update topic'
                                    }
                                />
                            </div>
                        </div>
                    </form>
                }
            />
        </div>
    );
};
const Wrap = styled.div`
    width: 100%;
`;
const Title = styled.p`
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    color: ${colors.black40};
    margin: 8px 0px;
`;
const Paragraph = styled.p`
    font-weight: 400;
    font-size: 18px;
    line-height: 19px;
    color: ${colors.black20};
`;
const TextArea = styled.div`
    background: #f7f9fa;
    border-radius: 6px;
    padding: 15px;
    font-size: 18px;
`;
